import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import MarqueeLink from "../components/MarqueeLink";
import { PROJECTS } from "../lib/projects";
import { IMAGES } from "../lib/assets";

const TABS = ["All", "Commercial", "Residential"];

export default function Projects() {
  const [tab, setTab] = useState("All");
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(id);
  }, [hash]);
  const filtered =
    tab === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === tab);

  return (
    <div data-testid="projects-page" className="bg-[#F5F5F0]">
      {/* HEADER with background image */}
      <section className="relative overflow-hidden -mt-32 md:-mt-36">
        <img
          src={IMAGES.commercialHall}
          alt="Architectural project"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-44 md:pt-56 pb-32 md:pb-44">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">Our Work</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-10 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-[22ch] leading-[1.1]">
              Projects across New Zealand.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* FILTER TABS */}
      <section className="border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex gap-10 py-8">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`projects-tab-${t.toLowerCase()}`}
              className={`relative font-heading uppercase tracking-[0.25em] text-[11px] font-semibold pb-2 transition-colors ${
                tab === t ? "text-[#EB1D25]" : "text-[#545454] hover:text-[#111111]"
              }`}
            >
              {t}
              <span
                className={`absolute left-0 right-0 bottom-0 h-px transition-all duration-300 ${
                  tab === t ? "bg-[#EB1D25]" : "bg-transparent"
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      {/* EXPANDED PROJECT BLOCKS */}
      {filtered.map((p, idx) => {
        const imageLeft = idx % 2 === 0;
        return (
          <section
            key={p.id}
            id={p.id}
            data-testid={`project-block-${p.id}`}
            className="py-24 md:py-40 border-t border-[#E2E2DF] scroll-mt-32"
          >
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
                <div
                  className={`md:col-span-7 ${
                    imageLeft ? "" : "md:order-2"
                  }`}
                >
                  <Reveal>
                    <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-[#111]">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                      />
                    </div>
                  </Reveal>
                  {p.gallery && p.gallery.length > 0 && (
                    <Reveal delay={160}>
                      <div className="mt-6 grid grid-cols-2 gap-6">
                        {p.gallery.map((src, i) => {
                          const isLastOdd =
                            p.gallery.length % 2 === 1 &&
                            i === p.gallery.length - 1;
                          return (
                            <div
                              key={i}
                              className={`overflow-hidden bg-[#111] ${
                                isLastOdd
                                  ? "col-span-2 aspect-[16/9]"
                                  : "aspect-[4/5]"
                              }`}
                            >
                              <img
                                src={src}
                                alt={`${p.name} ${i + 2}`}
                                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Reveal>
                  )}
                </div>

                <div
                  className={`md:col-span-5 md:pt-12 ${
                    imageLeft ? "" : "md:order-1"
                  }`}
                >
                  <Reveal delay={120}>
                    <span className="eyebrow-muted">
                      {String(idx + 1).padStart(2, "0")} — {p.category}
                    </span>
                    <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] leading-[1.15] font-medium tracking-tight">
                      {p.name}
                    </h2>
                    <p className="mt-3 text-[#545454] text-sm md:text-base">
                      {p.location}
                    </p>
                  </Reveal>

                  <Reveal delay={200}>
                    <p className="mt-8 text-[#545454] text-base md:text-lg leading-relaxed max-w-md">
                      {p.blurb}
                    </p>
                  </Reveal>

                  <Reveal delay={280}>
                    <div className="mt-10">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#545454] font-heading font-semibold mb-4">
                        Services
                      </p>
                      <ul>
                        {p.services.map((s, i) => (
                          <li
                            key={i}
                            className="py-3 border-t border-[#E2E2DF] text-[#111111] font-heading text-sm md:text-base"
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>

                  {p.page && (
                    <Reveal delay={360}>
                      <Link
                        to={p.page}
                        data-testid={`project-page-link-${p.id}`}
                        className="mt-10 inline-flex items-center gap-2 text-[#EB1D25] font-heading uppercase tracking-[0.22em] text-[11px] font-semibold border-b border-[#EB1D25] pb-1 hover:text-[#111111] hover:border-[#111111] transition-colors"
                      >
                        View project
                        <ArrowRight size={14} strokeWidth={1.5} />
                      </Link>
                    </Reveal>
                  )}

                  {p.link && (
                    <Reveal delay={360}>
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`project-link-${p.id}`}
                        className="mt-10 inline-flex items-center gap-2 text-[#EB1D25] font-heading uppercase tracking-[0.22em] text-[11px] font-semibold border-b border-[#EB1D25] pb-1 hover:text-[#111111] hover:border-[#111111] transition-colors"
                      >
                        Visit project
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                      </a>
                    </Reveal>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {filtered.length === 0 && (
        <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <p className="font-heading text-xl text-[#545454]">
              More projects in this category coming soon.
            </p>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] tracking-tight font-medium leading-[1.2] max-w-2xl">
                Have a project on the boards? Let's talk.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Reveal delay={120}>
              <MarqueeLink to="/contact" variant="ghost-dark" data-testid="projects-cta-contact">
                Start a project
              </MarqueeLink>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
